import clsx from 'clsx'
import { html, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { t } from '@/localization/localization'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './skeleton.scss?inline'

export type SkeletonVariant = 'text' | 'circular' | 'rectangular'
export type SkeletonAnimation = 'wave' | 'pulse' | 'custom'

/**
 * `dap-ds-skeleton`
 * @summary A skeleton loader component for displaying placeholder content while loading.
 * @element dap-ds-skeleton
 * @title - Skeleton
 *
 * @example Basic Usage
 * ```html
 * <dap-ds-skeleton></dap-ds-skeleton>
 * <dap-ds-skeleton variant="circular"></dap-ds-skeleton>
 * <dap-ds-skeleton variant="rectangular" width="200px" height="100px"></dap-ds-skeleton>
 * <dap-ds-skeleton animation="custom" custom-keyframes="0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); }"></dap-ds-skeleton>
 * ```
 *
 * @csspart base - The main skeleton container.
 *
 * @cssprop --dds-skeleton-color - The base color of the skeleton (default: linear-gradient(90deg, transparent, rgb(0 0 0 / 10%), transparent))
 * @cssprop --dds-skeleton-animation-duration - Duration of the loading animation (default: 1.5s)
 * @cssprop --dds-skeleton-border-radius - Border radius for rectangular skeletons (default: var(--dds-radius-small))
 * @cssprop --dds-skeleton-text-spacing - Spacing between text lines in text variant (default: var(--dds-spacing-100))
 * @cssprop --dds-skeleton-animation-timing-function - Timing function for the loading animation (default: ease-in-out)
 */
@customElement('dap-ds-skeleton')
export default class DapDSSkeleton extends DdsElement {
  /**
   * The variant of the skeleton.
   * @type {"text" | "circular" | "rectangular"}
   */
  @property({ type: String, reflect: true }) variant: SkeletonVariant = 'text'

  /**
   * The width of the skeleton. Can be any valid CSS width value.
   */
  @property({ type: String }) width?: string

  /**
   * The height of the skeleton. Can be any valid CSS height value.
   */
  @property({ type: String }) height?: string

  /**
   * Whether to animate the skeleton.
   */
  @property({ type: Boolean, reflect: true }) noAnimation = false

  /**
   * The animation type for the skeleton.
   * @type {"wave" | "pulse" | "custom"}
   */
  @property({ type: String, reflect: true }) animation: SkeletonAnimation =
    'wave'

  /**
   * Custom keyframes for the animation when animation="custom".
   * Should be a valid CSS keyframes string without the @keyframes wrapper.
   * @example "0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); }"
   */
  @property({ type: String, attribute: 'custom-keyframes' })
  customKeyframes?: string

  static readonly styles = unsafeCSS(styles)

  private _userCustomStyles?: string

  private _generateKeyframesCSS(): string | undefined {
    if (this.animation === 'custom' && this.customKeyframes) {
      return `
        @keyframes custom-skeleton-animation {
          ${this.customKeyframes}
        }
        .skeleton--custom::before {
          animation: custom-skeleton-animation var(--dds-skeleton-animation-duration) var(--dds-skeleton-animation-timing-function) infinite;
        }
      `
    }
    return undefined
  }

  private _updateMergedStyles() {
    const keyframesCSS = this._generateKeyframesCSS()
    const parts: string[] = []

    if (keyframesCSS) {
      parts.push(keyframesCSS)
    }

    if (this._userCustomStyles) {
      parts.push(this._userCustomStyles)
    }

    const mergedStyles = parts.length > 0 ? parts.join('\n') : undefined

    // Only update if different to avoid infinite loops
    if (mergedStyles !== this.customStyles) {
      // Use requestUpdate to properly trigger Lit's update cycle
      const oldValue = this.customStyles
      ;(this as any).customStyles = mergedStyles
      this.requestUpdate('customStyles', oldValue)
    }
  }

  protected updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(changedProperties)

    // Track user-provided customStyles (separate from generated keyframes)
    if (changedProperties.has('customStyles')) {
      const currentStyles = this.customStyles || ''
      const keyframesCSS = this._generateKeyframesCSS()

      if (keyframesCSS && currentStyles.includes(keyframesCSS)) {
        // Extract user's styles by removing our generated CSS
        this._userCustomStyles =
          currentStyles.replace(keyframesCSS, '').trim() || undefined
      } else {
        // No keyframes CSS, so all styles are user-provided
        this._userCustomStyles = currentStyles || undefined
      }
    }

    // Update merged styles when animation-related properties change
    if (
      changedProperties.has('animation') ||
      changedProperties.has('customKeyframes')
    ) {
      this._updateMergedStyles()
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    // Initialize user custom styles
    this._userCustomStyles = this.customStyles || undefined
    // Generate and set merged styles
    this._updateMergedStyles()
  }

  render() {
    if (this.width) {
      this.style.setProperty('--skeleton-width', this.width)
    } else {
      this.style.removeProperty('--skeleton-width')
    }

    if (this.height) {
      this.style.setProperty('--skeleton-height', this.height)
    } else {
      this.style.removeProperty('--skeleton-height')
    }

    return html`
      <div
        part="base"
        class="${clsx('skeleton', {
          [`skeleton--${this.variant}`]: this.variant,
          'skeleton--animate': !this.noAnimation,
          [`skeleton--${this.animation}`]: !this.noAnimation && this.animation,
        })}"
        role="status"
        aria-label=${this.ariaLabel ?? t('loading')}></div>
    `
  }
}
